import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  AlertAppearances,
  AlertVariants,
  ButtonVariants,
  Dropdown,
  Field,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import {
  CERTIFICATE_TYPE,
  buildCertificateTypeFilterOptions,
  buildMaterialTypeOptions,
} from '@/config/statuses';
import { GeneratedIdField } from '@/components/common';
import { formatNumber, formatQuantity } from '@/lib/format';
import { isVolumeUnit, normalizeUnit } from '@/lib/utils/unitConversion';
import { useTranslations } from '@/hooks';
import { useCreateMaterialMutation, useUpdateMaterialMutation } from '../../api/materialsApi';
import {
  EMPTY_MATERIAL_FORM,
  MATERIAL_FIELDS,
  MATERIAL_TYPES,
  buildDeclarationUnitOptions,
  buildWorkingUnitOptions,
} from '../../constants/materials.constants';
import {
  CONVERSION_KINDS,
  EXAMPLE_OPERATIONS,
  conversionExample,
  conversionRule,
} from '../../utils/materialUnits';
import styles from './MaterialModal.module.scss';

const ENERGY_MULTIPLIER = '1000';

const hasRequiredFields = (values) => {
  const hasIdentity =
    (values[MATERIAL_FIELDS.materialName] ?? '').trim() !== '' &&
    (values[MATERIAL_FIELDS.materialId] ?? '').trim() !== '' &&
    Boolean(values[MATERIAL_FIELDS.materialType]);

  // Conventional is infinite — it asks for nothing beyond its identity.
  if (values[MATERIAL_FIELDS.materialType] !== MATERIAL_TYPES.certified) return hasIdentity;

  const conversion = conversionRule(
    values[MATERIAL_FIELDS.declarationUnit],
    values[MATERIAL_FIELDS.workingUnit],
  );

  return (
    hasIdentity &&
    Boolean(values[MATERIAL_FIELDS.certificateType]) &&
    Boolean(values[MATERIAL_FIELDS.declarationUnit]) &&
    (!conversion?.required || (values[MATERIAL_FIELDS.conversionFactor] ?? '').trim() !== '')
  );
};

const TextField = ({ name, label, placeholder, required = false, disabled = false }) => (
  <Field name={name} required={required}>
    {({ id, name: inputName, value, onChange, onBlur, status, message }) => (
      <TextInput
        id={id}
        name={inputName}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        status={status}
        message={message}
        required={required}
        disabled={disabled}
      />
    )}
  </Field>
);

const DropdownField = ({
  name,
  label,
  placeholder,
  options,
  required = false,
  disabled = false,
}) => (
  <Field name={name} required={required}>
    {({ id, name: inputName, value, onChange, onBlur, status, message }) => (
      <Dropdown
        id={id}
        name={inputName}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        status={status}
        message={message}
        required={required}
        disabled={disabled}
        options={options}
      />
    )}
  </Field>
);

const EnergyContentRow = ({ declarationUnit, disabled }) => {
  const t = useTranslations();
  const energyUnit = isVolumeUnit(declarationUnit) ? t('UNIT_MJ_PER_LITRE') : t('UNIT_MJ_PER_KG');

  return (
    <div className={styles.energy}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {t('FORM_TOTAL_ENERGY_CONTENT')}
      </Typography>

      <div className={styles.energyRow}>
        <TextInput
          disabled
          className={styles.energyQuantity}
          label={t('FIELD_QUANTITY')}
          value={t('MATERIALS_ALLOCATING_QUANTITY')}
          aria-label={t('FIELD_QUANTITY')}
        />

        <Typography
          className={styles.energyOperator}
          variant={TypographyVariants.label}
          color={TypographyColors.secondary}
        >
          ×
        </Typography>

        <TextField
          name={MATERIAL_FIELDS.energyContent}
          label={t('MATERIALS_ENERGY_CONTENT', { unit: energyUnit })}
          placeholder={t('FORM_ENERGY_CONTENT_PLACEHOLDER')}
          disabled={disabled}
        />

        <Typography
          className={styles.energyOperator}
          variant={TypographyVariants.label}
          color={TypographyColors.secondary}
        >
          ×
        </Typography>

        <TextInput
          disabled
          className={styles.energyMultiplier}
          value={ENERGY_MULTIPLIER}
          aria-label={t('MATERIALS_ENERGY_MULTIPLIER')}
        />
      </div>
    </div>
  );
};

const EXAMPLE_FACTOR_FORMAT = Object.freeze({ useGrouping: false, maximumFractionDigits: 6 });
// Figma 232:10394 shows every result to three decimals ("10.000 gal").
const EXAMPLE_RESULT_FORMAT = Object.freeze({
  useGrouping: false,
  minimumFractionDigits: 3,
  maximumFractionDigits: 6,
});

/**
 * Props:
 *   example  a conversionExample() result — the worked shipment to show
 */
const ConversionExample = ({ example }) => {
  const t = useTranslations();

  const fields = useMemo(() => {
    const formulaKey =
      example.operation === EXAMPLE_OPERATIONS.multiply
        ? 'MATERIALS_EXAMPLE_MULTIPLY'
        : 'MATERIALS_EXAMPLE_DIVIDE';
    return [
      {
        id: 'shipment',
        label: t('MATERIALS_EXAMPLE_SHIPMENT'),
        value: formatQuantity({ amount: example.quantity, unit: example.declarationUnit }),
      },
      {
        id: 'converted',
        label: t('MATERIALS_EXAMPLE_IN_UNIT', { unit: example.workingUnit }),
        value: t(formulaKey, {
          quantity: example.quantity,
          factor: formatNumber(example.factor, EXAMPLE_FACTOR_FORMAT),
          result: formatNumber(example.result, EXAMPLE_RESULT_FORMAT),
          unit: example.workingUnit,
        }),
      },
    ];
  }, [t, example]);

  return (
    <div className={styles.example}>
      <div className={styles.exampleContent}>
        <div className={styles.exampleHeader}>
          <Typography variant={TypographyVariants.caption} color={TypographyColors.placeholder}>
            {t('MATERIALS_EXAMPLE_TITLE')}
          </Typography>
          <span className={styles.exampleRule} />
        </div>
        <div className={styles.exampleRow}>
          {fields.map((field) => (
            <div key={field.id} className={styles.exampleField}>
              <Typography variant={TypographyVariants.caption} color={TypographyColors.secondary}>
                {field.label}
              </Typography>
              <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.primary}>
                {field.value}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Props:
 *   declarationUnit  the selected declaration unit
 *   workingUnit      the selected working unit
 *   disabled         whether the form is saving
 */
const ConversionFactorField = ({ declarationUnit, workingUnit, disabled }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const rule = useMemo(
    () => conversionRule(declarationUnit, workingUnit),
    [declarationUnit, workingUnit],
  );

  const value = values[MATERIAL_FIELDS.conversionFactor];
  const pair = `${declarationUnit}>${workingUnit}`;
  const previousPair = useRef(pair);

  // A locked pair owns the field; any other pair keeps what a saved material already had.
  useEffect(() => {
    const changed = previousPair.current !== pair;
    previousPair.current = pair;

    if (rule?.kind === CONVERSION_KINDS.fixed) {
      if (value !== rule.factor) setValue(MATERIAL_FIELDS.conversionFactor, rule.factor);
      return;
    }

    if (changed && value) setValue(MATERIAL_FIELDS.conversionFactor, '');
  }, [pair, rule, value, setValue]);

  const label = useMemo(() => {
    if (rule?.kind === CONVERSION_KINDS.density) {
      return t('MATERIALS_DENSITY', {
        weightUnit: rule.weightUnit,
        volumeUnit: rule.volumeUnit,
      });
    }

    const units = {
      declarationUnit: normalizeUnit(declarationUnit),
      workingUnit: normalizeUnit(workingUnit),
    };

    return rule?.kind === CONVERSION_KINDS.discrete
      ? t('MATERIALS_CONVERSION_FACTOR_DISCRETE', units)
      : t('MATERIALS_CONVERSION_FACTOR_FIXED', units);
  }, [t, rule, declarationUnit, workingUnit]);

  const example = useMemo(
    () => conversionExample(rule, declarationUnit, workingUnit, value),
    [rule, declarationUnit, workingUnit, value],
  );

  if (!rule) return null;

  return (
    <>
      <TextField
        required={rule.required}
        name={MATERIAL_FIELDS.conversionFactor}
        label={label}
        placeholder={
          rule.kind === CONVERSION_KINDS.density
            ? t('FORM_DENSITY_PLACEHOLDER')
            : t('FORM_CONVERSION_FACTOR_PLACEHOLDER')
        }
        disabled={disabled || rule.locked}
      />
      {example && <ConversionExample example={example} />}
    </>
  );
};

/**
 * Props:
 *   saving  whether the form is saving
 *   isEdit  editing a saved material rather than adding one
 */
const MaterialFields = ({ saving, isEdit }) => {
  const t = useTranslations();
  const { values } = useFormState();

  const materialType = values[MATERIAL_FIELDS.materialType];
  const certificateType = values[MATERIAL_FIELDS.certificateType];
  const declarationUnit = values[MATERIAL_FIELDS.declarationUnit];
  const workingUnit = values[MATERIAL_FIELDS.workingUnit];

  const isCertified = materialType === MATERIAL_TYPES.certified;
  const isConventional = materialType === MATERIAL_TYPES.conventional;
  const showUnits = isCertified && Boolean(certificateType);

  const materialTypeOptions = useMemo(() => buildMaterialTypeOptions(t), [t]);
  const certificateTypeOptions = useMemo(() => buildCertificateTypeFilterOptions(t), [t]);
  const declarationUnitOptions = useMemo(() => buildDeclarationUnitOptions(t), [t]);
  const workingUnitOptions = useMemo(
    () => buildWorkingUnitOptions(t, declarationUnit),
    [t, declarationUnit],
  );

  return (
    <div className={styles.fields}>
      <TextField
        required
        name={MATERIAL_FIELDS.materialName}
        label={t('FIELD_MATERIAL_NAME')}
        placeholder={t('FORM_MATERIAL_NAME_PLACEHOLDER')}
        disabled={saving}
      />

      <Form.Row columns={2}>
        <GeneratedIdField
          name={MATERIAL_FIELDS.materialId}
          label={t('FIELD_MATERIAL_ID')}
          placeholder={t('FORM_MATERIAL_ID_PLACEHOLDER')}
          readOnly={isEdit}
        />
        <TextField
          name={MATERIAL_FIELDS.referenceId}
          label={t('FORM_REFERENCE_ID_OPTIONAL')}
          placeholder={t('FORM_REFERENCE_ID_PLACEHOLDER')}
          disabled={saving}
        />
      </Form.Row>

      <DropdownField
        required
        name={MATERIAL_FIELDS.materialType}
        label={t('FIELD_MATERIAL_TYPE')}
        placeholder={t('FORM_SELECT_MATERIAL_TYPE')}
        options={materialTypeOptions}
        disabled={saving}
      />

      {isCertified && (
        <DropdownField
          required
          name={MATERIAL_FIELDS.certificateType}
          label={t('FIELD_CERTIFICATION_TYPE')}
          placeholder={t('FORM_SELECT_CERTIFICATION_TYPE')}
          options={certificateTypeOptions}
          disabled={saving}
        />
      )}

      {isConventional && (
        <Alert variant={AlertVariants.info} appearance={AlertAppearances.accent}>
          {t('MATERIALS_CONVENTIONAL_INFO')}
        </Alert>
      )}

      {showUnits && (
        <>
          <Form.Row columns={2}>
            <DropdownField
              required
              name={MATERIAL_FIELDS.declarationUnit}
              label={t('FIELD_DECLARATION_UNIT')}
              placeholder={t('FORM_SELECT_DECLARATION_UNIT')}
              options={declarationUnitOptions}
              disabled={saving}
            />
            <DropdownField
              name={MATERIAL_FIELDS.workingUnit}
              label={t('FORM_WORKING_UNIT_OPTIONAL')}
              placeholder={t('FORM_SELECT_WORKING_UNIT')}
              options={workingUnitOptions}
              disabled={saving}
            />
          </Form.Row>

          <ConversionFactorField
            declarationUnit={declarationUnit}
            workingUnit={workingUnit}
            disabled={saving}
          />

          {certificateType === CERTIFICATE_TYPE.isccEu && (
            <EnergyContentRow declarationUnit={declarationUnit} disabled={saving} />
          )}
        </>
      )}
    </div>
  );
};

/**
 * Props:
 *   open      whether the modal is shown
 *   onClose   close handler (✕ / Cancel)
 *   material  the material being edited as { id, values }; omit to add a new one
 */
export const MaterialModal = ({ open, onClose, material }) => {
  const t = useTranslations();
  const [createMaterial, { isLoading: isCreating }] = useCreateMaterialMutation();
  const [updateMaterial, { isLoading: isUpdating }] = useUpdateMaterialMutation();

  const isEdit = Boolean(material);
  const saving = isCreating || isUpdating;

  const initialValues = useMemo(
    () => ({ ...EMPTY_MATERIAL_FORM, ...(material?.values ?? {}) }),
    [material?.values],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = isEdit
        ? await updateMaterial({ id: material.id, values })
        : await createMaterial(values);

      if (result.error) return;
      onClose();
    },
    [isEdit, material?.id, createMaterial, updateMaterial, onClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={isEdit ? t('MATERIALS_EDIT_TITLE') : t('MATERIALS_ADD')}
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: t('COMMON_SAVE'),
              variant: ButtonVariants.primary,
              disabled: saving || !hasRequiredFields(values),
              onClick: submit,
            },
          ]}
        >
          <MaterialFields saving={saving} isEdit={isEdit} />
        </Modal>
      )}
    </Form>
  );
};
